import {
  getAuth,
  signInWithEmailAndPassword,
  updatePassword,
  isSignInWithEmailLink,
  IdTokenResult,
} from 'firebase/auth';
import asyncRetry from 'async-retry';
import { getFetcher, patchFetcher, postFetcher } from '@s/io/swrFetcher';
import { apiEndpoint } from '@c/config';
import { genericError } from '@c/application/GenericError';
import { convertOperatorMeResponse, RawGetMeResponse } from '@c/domain/entities/Operators';

export async function retryInviteOperator(url: string) {
  await postFetcher(url, {});
}

export async function inviteOperator(
  url: string,
  { arg }: { arg: { email: string; roleType: string } }
) {
  await postFetcher(url, {
    email: arg.email,
    operator_role_type: arg.roleType,
  });
}

export async function updateOperator(
  url: string,
  { arg }: { arg: { displayName: string; roleType: string; assignedProjectIds: string[] } }
) {
  await patchFetcher(url, {
    display_name: arg.displayName,
    operator_role_type: arg.roleType,
    assigned_project_ids: arg.assignedProjectIds,
  });
}

export async function updateOperatorDisabled(
  url: string,
  { arg }: { arg: { isDisabled: boolean } }
) {
  await patchFetcher(url, {
    disabled: arg.isDisabled,
  });
}

export async function issueOperator({
  password,
  email,
  displayName,
}: {
  password: string;
  email: string;
  displayName: string;
}): Promise<void> {
  if (!isSignInWithEmailLink(getAuth(), location.href)) {
    throw genericError({ message: 'メールリンクのURLが期限切れ、または不正です' });
  }

  const user = getAuth().currentUser;
  if (!user) {
    throw genericError({ message: '認証に失敗しました。もう一度試してください。' });
  }

  await asyncRetry(
    async () => {
      await updatePassword(getAuth().currentUser!, password);
    },
    { retries: 2 }
  );

  await asyncRetry(
    async () => {
      await signInWithEmailAndPassword(getAuth(), email, password);
    },
    { retries: 2 }
  );

  let idTokenResult: IdTokenResult;
  try {
    idTokenResult = await asyncRetry(async () => getAuth().currentUser!.getIdTokenResult(true), {
      retries: 2,
    });
  } catch (e) {
    throw genericError({ message: 'トークンの取得に失敗しました。もう一度試してください。' });
  }

  await asyncRetry(
    async () => {
      const res = await fetch(apiEndpoint(`/operators/me`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idTokenResult.token}`,
        },
        body: JSON.stringify({ display_name: displayName, email }),
      });
      if (!res.ok) {
        throw genericError({
          message: 'オペレーターの更新に失敗しました。もう一度試してください。',
        });
      }
    },
    { retries: 2 }
  );

  // patch /operator/meでfirebase auth上userの情報が更新され、クライアント側で古いuser情報が残ってしまうためuser情報をreloadする
  try {
    await asyncRetry(async () => getAuth().currentUser!.reload(), {
      retries: 2,
    });
  } catch (e) {
    throw genericError({ message: 'userの情報のreloadに失敗しました。' });
  }
}

export async function updateMyOperator(
  url: string,
  { arg }: { arg: { displayName?: string; email?: string } }
) {
  await patchFetcher(url, {
    display_name: arg.displayName,
    email: arg.email,
  });
}

export async function verify(url: string, { arg }: { arg: { email: string; oobCode: string } }) {
  await postFetcher(url, { email: arg.email, oob_code: arg.oobCode });
}

export async function initialUpdate(
  url: string,
  { arg }: { arg: { displayName: string; password: string } }
) {
  await patchFetcher(url, { display_name: arg.displayName, password: arg.password });
}

export async function createLoginEventLog(url: string) {
  await postFetcher(url, {});
}

export async function getMe(url: string) {
  const data = await getFetcher<RawGetMeResponse>(url);
  return convertOperatorMeResponse(data);
}
